import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { ProductCollectionSegment } from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { CollectionInfo } from "@redotech/shopify-storefront";
import {
  convertShopifyIdToGid,
  Gid,
  parseNumericIdFromShopifyId,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memo, useState } from "react";
import { OperatorSelector } from "../../operator-selector";
import { CollectionSelectionModal } from "./collection-selection-modal";
import * as css from "./product-collections.module.css";

interface ProductCollectionsSegmentCommonProps {
  condition: ProductCollectionSegment;
  getCollectionsByGids: (ids: Gid[]) => Promise<CollectionInfo[]>;
}

export type ProductCollectionsSegmentProps =
  ProductCollectionsSegmentCommonProps &
    (
      | {
          readonly: false;
          setCondition: (condition: ProductCollectionSegment) => void;
          searchCollections: (params: {
            search: string;
          }) => Promise<CollectionInfo[]>;
        }
      | { readonly: true }
    );

const cartOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "At least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

const collectionListOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "in any of",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "in none of",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "in all of",
};

export const ProductCollectionsSegmentCondition = memo(
  function ProductCollectionsSegmentCondition({
    condition,
    getCollectionsByGids,
    ...props
  }: ProductCollectionsSegmentProps) {
    const [modalOpen, setModalOpen] = useState(false);

    // Load collection names for display
    const collectionNamesLoad = useLoad(async () => {
      if (condition.collectionIds.length === 0) {
        return {};
      }

      const gids = condition.collectionIds.map((id) =>
        convertShopifyIdToGid(id, ShopifyIdType.Collection),
      );
      const collections = await getCollectionsByGids(gids);
      const nameMap: { [gid: string]: string } = {};
      for (const collection of collections) {
        nameMap[collection.id] = collection.title;
      }

      return nameMap;
    }, [condition.collectionIds, getCollectionsByGids]);

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, cartOperator });
        }
      },
    );

    const handleCollectionListOperatorSelected = useHandler(
      (collectionListOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, collectionListOperator });
        }
      },
    );

    const handleCollectionsSelected = useHandler((collectionIds: string[]) => {
      if (!props.readonly) {
        props.setCondition({ ...condition, collectionIds });
      }
      setModalOpen(false);
    });

    // Show loading animation while collection names are being fetched
    if (condition.collectionIds.length > 0 && !collectionNamesLoad.value) {
      return (
        <Flex align="center" dir="row" justify="center" p="md" w="full">
          <LoadingRedoAnimation className={css.smallSpinner} />
        </Flex>
      );
    }

    return (
      <>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCartOperatorSelected}
          operatorToText={cartOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.cartOperator}
        />
        <Text fontSize="xs" textColor="primary">
          product in the cart is
        </Text>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCollectionListOperatorSelected}
          operatorToText={collectionListOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.collectionListOperator}
        />
        <Text fontSize="xs" textColor="primary">
          the collections in this list:
        </Text>
        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {condition.collectionIds.length === 0 ? (
            <Text fontSize="xs" textColor="secondary">
              No collections selected
            </Text>
          ) : (
            condition.collectionIds.map((collectionId, index) => {
              const collectionName =
                collectionNamesLoad.value?.[collectionId] ||
                parseNumericIdFromShopifyId(collectionId);

              return (
                <RedoBadge
                  color="gray"
                  key={index}
                  size="sm"
                  text={collectionName}
                />
              );
            })
          )}
        </Flex>
        {!props.readonly && (
          <Flex justify="flex-end" w="full">
            <RedoButton
              hierarchy="secondary"
              IconLeading={EditPencilIcon}
              onClick={() => setModalOpen(true)}
              size="xs"
              text="Edit list"
            />
          </Flex>
        )}
        {!props.readonly && modalOpen && (
          <CollectionSelectionModal
            onClose={() => setModalOpen(false)}
            onCollectionsSelected={handleCollectionsSelected}
            searchCollections={props.searchCollections}
            selectedCollections={condition.collectionIds}
          />
        )}
      </>
    );
  },
);
